import type { MatrixPos, Task } from "@app/core";
import { matrixIndexOf } from "@app/core";
import { Check, Clock, EyeOff, cn } from "@app/ui";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { I18nKey } from "../../lib/language.js";
import { AnimatePresence, motion, springs, useReducedMotion } from "../../lib/motion/index.js";
import { ShowMoreFooter } from "../../lib/paged-list.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useCoarsePointer } from "../../lib/useMobileUx.js";
import { useI18n } from "../../lib/useI18n.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useSettingsStore } from "../../stores/settings.store.js";
import { matrixCellWash, matrixColorHex, matrixScoreOf, PRIORITY_FLAG_HEX } from "../task-colors.js";
import { buildAdaptiveBoardLayout, expandedClusterHeight, reconcileBoardLayout, type BoardLayout, type BoardLayoutNode } from "./board-layout.js";
import type { ViewItem } from "./model.js";
import { staleWeeks } from "./model.js";
import { posFromPoint, useBoardDrag, type BoardDrag, type DropBand } from "./useBoardDrag.js";

interface Props {
  /** Positioned items (placed dots + flag-only ghosts). */
  items: ViewItem[];
  /** Position-less items shown in the triage tray below the board. */
  tray: ViewItem[];
  /** Minute-fresh clock for the stale-age badges. */
  now: Date;
  onPlace: (item: ViewItem, pos: MatrixPos) => void;
  /** Take a PLACED item off the board (dropped on the tray zone) — position AND
   *  the mirrored priority flag are cleared, like the form's Clear. */
  onUnplace: (item: ViewItem) => void;
  onOpen: (task: Task) => void;
  /** Take the item's whole series off the decision surfaces (`matrix_hidden`).
   *  Fired by the eye control that appears at a chip's right edge on hover. */
  onHide: (item: ViewItem) => void;
  /** A chip released over a load-strip day (`data-strip-day`) — the release
   *  point is passed along so the time ladder can open right there. */
  onDropOnDay: (item: ViewItem, dayKey: string, at: { x: number; y: number }) => void;
  /** Live strip-day hover while dragging, so the strip can highlight the target. */
  onHoverDay: (dayKey: string | null) => void;
  /** A chip released over a queue band section (`data-queue-band`) — coarse
   *  placement at that band's diagonal center. */
  onDropOnBand: (item: ViewItem, band: DropBand) => void;
  /** Live band hover while dragging, so the queue can highlight the target. */
  onHoverBand: (band: DropBand | null) => void;
  /** Whether a chip drag is in flight (the queue materializes empty bands). */
  onDragActive: (active: boolean) => void;
}

/** Band/level label per diagonal third — mirrors the model's `BAND_BY_INDEX`. */
const BAND_KEY: I18nKey[] = ["priorities.band.later", "priorities.band.next", "priorities.band.now"];
const LEVEL_KEY: I18nKey[] = ["sidebar.priority.low", "sidebar.priority.medium", "sidebar.priority.high"];

/** Breathing room an expanded block keeps from the window edges. */
const VIEWPORT_MARGIN = 12;

/** Tray chips rendered before a "show more" — see the note at `trayShown`. */
const TRAY_PAGE = 100;

interface ExpandedPlacement {
  /** Which end of the block sits on its anchor point. */
  align: "center" | "top" | "bottom";
  maxHeight: number;
}

const ALIGN_TRANSLATE: Record<ExpandedPlacement["align"], string> = {
  center: "-50%",
  top: "0%",
  bottom: "-100%",
};

/**
 * The big importance×urgency board: every in-range task as a draggable chip at
 * its effective matrix position over the familiar 3×3 gradient. Drag a chip to
 * re-prioritize (writes an exact manual position — the projected band/priority
 * previews live under the board), onto a load-strip day to schedule it there,
 * or onto the tray zone to take it off the board; drag a tray chip onto the
 * board to triage it; click opens the editor. Corner captions carry the
 * Eisenhower guidance.
 *
 * Motion is deliberately local (hover/press and node enter/exit). Shared-layout
 * projection across every chip made a one-item release synchronously measure
 * and project the whole board, which violated the interaction budget.
 */
export const MatrixBoard = memo(function MatrixBoard({ items, tray, now, onPlace, onUnplace, onOpen, onHide, onDropOnDay, onHoverDay, onDropOnBand, onHoverBand, onDragActive }: Props) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const textScale = useSettingsStore((state) => state.textScale);
  const coarsePointer = useCoarsePointer();
  // ONE coordinate space for the whole board: the chip surface is measured,
  // positioned, AND pointer-mapped. Measuring the grid instead would report its
  // border box while chips live in its padding box — a systematic 1px skew.
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [gridSize, setGridSize] = useState({ width: 0, height: 0 });
  const [expandedClusterId, setExpandedClusterId] = useState<string | null>(null);
  const byId = useMemo(() => new Map([...items, ...tray].map((i) => [i.task.id, i])), [items, tray]);
  const { dragging, onPointerDown, onPointerMove, onPointerEnd } = useBoardDrag({
    gridRef: surfaceRef, itemOf: (id) => byId.get(id), onPlace, onUnplace, onOpen,
    onDropOnDay, onHoverDay, onDropOnBand, onHoverBand, onDragActive,
  });

  const draggedItem = dragging ? byId.get(dragging.id) : null;
  const dragPos = dragging ? posFromPoint(dragging.x, dragging.y, dragging.rect) : null;
  const overBoard = !!dragging?.overBoard;
  const hotCol = dragPos && overBoard ? matrixIndexOf(dragPos.urgency) : null;
  const hotRow = dragPos && overBoard ? matrixIndexOf(dragPos.importance) : null;
  /** Live projection of what releasing here would mean (band + derived priority). */
  const previewScore = dragPos && overBoard ? matrixScoreOf(dragPos) : null;
  // While a PLACED chip is dragged the tray doubles as a remove-from-board zone,
  // so it is shown even when empty.
  const showZone = tray.length > 0 || !!draggedItem?.placed;
  const zoneArmed = !!draggedItem?.placed;
  // The tray holds every unplaced in-range item — thousands on a real backlog of
  // undated tasks, and each chip is a drag root with its own hover control. It
  // was the single largest render on this route; cap it like the queue bands.
  const [trayLimit, setTrayLimit] = useState(TRAY_PAGE);
  const trayShown = useMemo(
    () => (tray.length <= trayLimit ? tray : tray.slice(0, trayLimit)),
    [tray, trayLimit],
  );
  useLayoutEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;
    // Repacking is O(groups × spiral × placed), so a window drag must not run
    // it per observer callback — coalesce to one measure per frame.
    let frame = 0;
    const measure = () => {
      frame = 0;
      const rect = surface.getBoundingClientRect();
      setGridSize((current) => (
        Math.abs(current.width - rect.width) < 0.5 && Math.abs(current.height - rect.height) < 0.5
          ? current
          : { width: rect.width, height: rect.height }
      ));
    };
    measure();
    const observer = new ResizeObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    });
    observer.observe(surface);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);
  const metrics = useMemo(() => ({ textScale, coarsePointer }), [textScale, coarsePointer]);
  // The packer rebuilds every node from scratch, so a one-chip drop would hand
  // the renderer a fresh object per node and re-render the whole board's motion
  // wrappers. `reconcileBoardLayout` carries identity over for the nodes the
  // repack left alone, which is what lets `BoardNode` below memoize.
  const previousLayout = useRef<BoardLayout | null>(null);
  const layout = useMemo(
    () => {
      const next = buildAdaptiveBoardLayout(
        items.map((item) => ({
          id: item.task.id,
          title: item.task.title || t("agenda.untitled"),
          pos: item.pos!,
          ...(item.stale
            ? { trailingText: t("priorities.stale.badge", { weeks: staleWeeks(item.task, now) }) }
            : {}),
          trailingIconCount: item.placed && item.pos?.urgencyAuto ? 1 : 0,
        })),
        gridSize.width,
        gridSize.height,
        metrics,
      );
      const merged = reconcileBoardLayout(previousLayout.current, next);
      previousLayout.current = merged;
      return merged;
    },
    [items, gridSize, now, t, metrics],
  );
  // Same idea one level down: a node's resolved items must keep array identity
  // when its members are unchanged, or every `BoardNode` prop check would fail.
  const previousNodeItems = useRef<Map<string, ViewItem[]>>(new Map());
  const nodeItems = useMemo(() => {
    const next = new Map<string, ViewItem[]>();
    for (const node of layout.nodes) {
      const resolved = node.itemIds
        .map((id) => byId.get(id))
        .filter((item): item is ViewItem => !!item);
      const old = previousNodeItems.current.get(node.id);
      next.set(node.id, old && old.length === resolved.length && old.every((item, i) => item === resolved[i])
        ? old
        : resolved);
    }
    previousNodeItems.current = next;
    return next;
  }, [layout, byId]);
  useEffect(() => {
    if (!expandedClusterId || layout.nodes.some((node) => node.id === expandedClusterId)) return;
    setExpandedClusterId(null);
  }, [expandedClusterId, layout.nodes]);
  const expandedNode = expandedClusterId
    ? layout.nodes.find((node) => node.id === expandedClusterId) ?? null
    : null;
  const expandedAnchorY = expandedNode?.y ?? null;
  const expandedCount = expandedNode?.itemIds.length ?? 0;
  // An expanded block is centred on its anchor by default, but near a board
  // edge that would push it off-screen — and it is absolutely positioned, so
  // the user cannot scroll to it. Grow away from the nearer edge instead and
  // cap the height to the room that direction actually has.
  const [expandedPlacement, setExpandedPlacement] = useState<ExpandedPlacement | null>(null);
  const placementFor = useCallback((anchorY: number, count: number): ExpandedPlacement | null => {
    const surface = surfaceRef.current;
    if (!surface) return null;
    const anchor = surface.getBoundingClientRect().top + anchorY;
    const ceiling = Math.min(window.innerHeight * 0.7, 480, window.innerHeight - VIEWPORT_MARGIN * 2);
    const desired = Math.min(expandedClusterHeight(count, metrics), ceiling);
    const above = Math.max(0, anchor - VIEWPORT_MARGIN);
    const below = Math.max(0, window.innerHeight - VIEWPORT_MARGIN - anchor);
    return desired / 2 <= above && desired / 2 <= below
      ? { align: "center", maxHeight: desired }
      : below >= above
        ? { align: "top", maxHeight: Math.min(desired, below) }
        : { align: "bottom", maxHeight: Math.min(desired, above) };
  }, [metrics]);
  // Placement is resolved EAGERLY in the same update that opens a block (see
  // `openCluster`). Resolving it in an effect instead re-rendered the open
  // block one commit later with a different alignment translate and height cap,
  // which teleported it by half its height and retargeted the growth mid-flight.
  // This effect only keeps an OPEN block placed as the window changes.
  useLayoutEffect(() => {
    if (expandedAnchorY === null) {
      setExpandedPlacement(null);
      return;
    }
    const place = () => setExpandedPlacement((current) => {
      const next = placementFor(expandedAnchorY, expandedCount);
      return next && current && next.align === current.align && next.maxHeight === current.maxHeight
        ? current
        : next;
    });
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [expandedAnchorY, expandedCount, placementFor]);
  // Stable so a `BoardNode` never re-renders merely because its expand handler
  // was re-created.
  const handleExpandedChange = useCallback((node: BoardLayoutNode, next: boolean) => {
    if (!next) {
      setExpandedClusterId(null);
      return;
    }
    setExpandedPlacement(placementFor(node.y, node.itemIds.length));
    setExpandedClusterId(node.id);
  }, [placementFor]);
  useEffect(() => {
    if (!expandedClusterId) return;
    const dismiss = (event: PointerEvent) => {
      const cluster = event.target instanceof Element
        ? event.target.closest("[data-matrix-cluster]")
        : null;
      if (cluster?.getAttribute("data-matrix-cluster") !== expandedClusterId) {
        setExpandedClusterId(null);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpandedClusterId(null);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [expandedClusterId]);
  const hintProps = {
    initial: reduce ? false : { opacity: 0, y: 2 },
    animate: { opacity: 1, y: 0 },
    transition: springs.snappy,
  } as const;

  return (
    <div className="flex select-none flex-col gap-2">
      <div className="flex items-stretch gap-1">
        {/* Importance axis */}
        <div className="flex w-4 shrink-0 flex-col items-center justify-between py-2">
          <span className="rotate-180 text-[calc(11px*var(--app-text-scale))] lowercase leading-none text-muted-foreground/90 [writing-mode:vertical-rl]">
            {t("matrix.high")}
          </span>
          <span className="rotate-180 text-xs font-medium uppercase tracking-wider text-muted-foreground [writing-mode:vertical-rl]">
            {t("matrix.importance")}
          </span>
          <span className="rotate-180 text-[calc(11px*var(--app-text-scale))] lowercase leading-none text-muted-foreground/90 [writing-mode:vertical-rl]">
            {t("matrix.low")}
          </span>
        </div>
        <div
          role="group"
          // Radius matches the flush `Card` around it, so the two borders that
          // meet at the top/right corner trace the same curve.
          className="relative grid aspect-square min-w-0 flex-1 grid-cols-3 grid-rows-3 gap-[3px] rounded-lg border bg-border/40 p-[3px]"
          aria-label={t("matrix.title")}
        >
          {[2, 1, 0].map((row) =>
            [0, 1, 2].map((col) => {
              const { color, wash } = matrixCellWash(col, row);
              const hot = hotCol === col && hotRow === row;
              return (
                <div
                  key={`${col}-${row}`}
                  className="rounded-md bg-background transition-[box-shadow] duration-150"
                  style={{
                    backgroundImage: `linear-gradient(0deg, ${wash}, ${wash})`,
                    boxShadow: hot ? `inset 0 0 0 2px ${color}` : undefined,
                  }}
                />
              );
            }),
          )}
          {/* Eisenhower guidance in the corners */}
          <Corner className="left-2 top-1.5">{t("priorities.corner.schedule")}</Corner>
          <Corner className="right-2 top-1.5 text-right">{t("priorities.corner.doNow")}</Corner>
          <Corner className="bottom-1.5 left-2">{t("priorities.corner.later")}</Corner>
          <Corner className="bottom-1.5 right-2 text-right">{t("priorities.corner.squeeze")}</Corner>

          {items.length === 0 && tray.length === 0 && (
            <motion.div
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <span className="rounded-md bg-background/85 px-3 py-1.5 text-xs text-muted-foreground shadow-sm">
                {t("priorities.board.empty")}
              </span>
            </motion.div>
          )}

          <div ref={surfaceRef} className="absolute inset-0">
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
              aria-hidden
            >
              {layout.nodes.map((node) => (node.displaced ? (
                <AnchorLeader
                  key={`anchor-${node.id}`}
                  node={node}
                  width={gridSize.width}
                  height={gridSize.height}
                />
              ) : null))}
            </svg>
            {/* `presenceAffectsLayout` defaults to TRUE, which clones a fresh
                presence-context object for EVERY child on every render — that
                context change re-renders each chip's `motion` element straight
                through `BoardNode`'s memo, and it was the single biggest cost of
                a drop (a board's worth of framer re-renders for one moved chip).
                Chip positions come from the pure packer as absolute left/top, so
                a chip entering or leaving cannot reflow its siblings and nothing
                needs that invalidation. */}
            <AnimatePresence initial={false} presenceAffectsLayout={false}>
              {layout.nodes.map((node) => {
                const nodes = nodeItems.get(node.id);
                if (!nodes || nodes.length === 0) return null;
                const expanded = expandedClusterId === node.id;
                // The dragged flag is resolved HERE, as a boolean, rather than by
                // handing every node the drag state: a drag start/end changes one
                // node's answer, so only that node re-renders.
                const draggedId = dragging?.id ?? null;
                return (
                  <BoardNode
                    key={node.id}
                    node={node}
                    items={nodes}
                    now={now}
                    draggedMemberId={
                      draggedId && nodes.some((item) => item.task.id === draggedId) ? draggedId : null
                    }
                    expanded={expanded}
                    placement={expanded ? expandedPlacement : null}
                    coarsePointer={coarsePointer}
                    reduce={!!reduce}
                    onExpandedChange={handleExpandedChange}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerEnd={onPointerEnd}
                    onOpen={onOpen}
                    onHide={onHide}
                  />
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </div>
      {/* Urgency axis */}
      <div className="ml-5 flex items-center justify-between px-1">
        <span className="text-[calc(11px*var(--app-text-scale))] lowercase text-muted-foreground/90">{t("matrix.low")}</span>
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t("matrix.urgency")}</span>
        <span className="text-[calc(11px*var(--app-text-scale))] lowercase text-muted-foreground/90">{t("matrix.high")}</span>
      </div>
      {dragging?.overDay ? (
        <motion.p key="hint-day" className="ml-5 px-1 text-xs font-medium" aria-live="polite" {...hintProps}>
          {t("priorities.strip.dropSchedule")}
        </motion.p>
      ) : dragging?.overBand ? (
        <motion.p key="hint-band" className="ml-5 px-1 text-xs font-medium" aria-live="polite" {...hintProps}>
          {t("priorities.queue.dropBand", { band: t(BAND_KEY[["later", "next", "now"].indexOf(dragging.overBand)]!) })}
        </motion.p>
      ) : previewScore !== null ? (
        <motion.p key="hint-score" className="ml-5 flex items-center gap-1.5 px-1 text-xs font-medium" aria-live="polite" {...hintProps}>
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: matrixColorHex(previewScore) }} />
          {t(BAND_KEY[matrixIndexOf(previewScore)]!)} · {t(LEVEL_KEY[matrixIndexOf(previewScore)]!)}
        </motion.p>
      ) : (
        <motion.p key="hint-idle" className="ml-5 px-1 text-xs text-muted-foreground" {...hintProps}>
          {t("priorities.board.hint")}
        </motion.p>
      )}

      {showZone && (
        <motion.div
          data-matrix-tray-zone
          layout={!reduce}
          transition={springs.smooth}
          className={cn(
            "ml-5 mt-1 rounded-lg border border-dashed p-2 transition-colors",
            // A drop target needs real size — pad up + center while armed.
            zoneArmed && "flex min-h-14 items-center justify-center text-center",
            zoneArmed && (dragging?.overZone
              ? "border-primary bg-primary/10"
              : "border-primary/50"),
          )}
        >
          <p className={cn("text-xs font-medium text-muted-foreground", tray.length > 0 && "mb-1.5")}>
            {zoneArmed ? (
              t("priorities.unplace.hint")
            ) : (
              <>
                {t("priorities.tray.title", { count: tray.length })}
                <span className="ml-2 font-normal">{t("priorities.tray.hint")}</span>
              </>
            )}
          </p>
          {tray.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {trayShown.map((item) => {
                const isDragged = dragging?.id === item.task.id;
                return (
                  <div
                    key={item.task.id}
                    data-matrix-drag-root
                    className="group relative"
                    style={isDragged ? { zIndex: 50 } : undefined}
                  >
                    <BoardChip
                      item={item}
                      now={now}
                      inTray
                      dragged={isDragged}
                      coarsePointer={coarsePointer}
                      onPointerDown={onPointerDown}
                      onPointerMove={onPointerMove}
                      onPointerEnd={onPointerEnd}
                      onOpen={onOpen}
                    />
                    {!isDragged && <HideControl item={item} onHide={onHide} />}
                  </div>
                );
              })}
            </div>
          )}
          {trayShown.length < tray.length && (
            <ShowMoreFooter
              shown={trayShown.length}
              total={tray.length}
              onShowMore={() => setTrayLimit((n) => n + TRAY_PAGE)}
              className="mt-1.5 border-t-0 px-0 py-1"
            />
          )}
        </motion.div>
      )}
    </div>
  );
});

function Corner({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span className={cn("pointer-events-none absolute max-w-[42%] text-[calc(11px*var(--app-text-scale))] font-medium uppercase leading-tight tracking-wide text-muted-foreground/85", className)}>
      {children}
    </span>
  );
}

/** Dotted leader from a displaced node back to its true matrix position.
 *  Memoized on the identity-stable node for the same reason as {@link BoardNode}:
 *  a repack must not rebuild a board's worth of SVG for one moved chip. */
const AnchorLeader = memo(function AnchorLeader(
  { node, width, height }: { node: BoardLayoutNode; width: number; height: number },
) {
  const anchorX = node.pos.urgency * width;
  const anchorY = (1 - node.pos.importance) * height;
  return (
    <g className="text-foreground/25">
      <line
        x1={anchorX}
        y1={anchorY}
        x2={node.x}
        y2={node.y}
        stroke="currentColor"
        strokeWidth="1"
        strokeDasharray="2 2"
      />
      <circle cx={anchorX} cy={anchorY} r="2.25" fill="currentColor" />
    </g>
  );
});

interface BoardNodeProps {
  node: BoardLayoutNode;
  items: ViewItem[];
  now: Date;
  /** Id of this node's member currently being dragged, else null. A boolean-ish
   *  primitive so the memo only re-renders the node the drag actually touches. */
  draggedMemberId: string | null;
  expanded: boolean;
  placement: ExpandedPlacement | null;
  coarsePointer: boolean;
  reduce: boolean;
  onExpandedChange: (node: BoardLayoutNode, expanded: boolean) => void;
  onPointerDown: (e: React.PointerEvent, id: string) => void;
  onPointerMove: (e: React.PointerEvent, id: string) => void;
  onPointerEnd: (e: React.PointerEvent, id: string) => void;
  onOpen: (task: Task) => void;
  onHide: (item: ViewItem) => void;
}

/**
 * One positioned board node — a single chip or a cluster block — inside its
 * `motion` wrapper.
 *
 * Memoized, and that memo is load-bearing: the packer rebuilds the layout on
 * every model change, so without identity-stable nodes (`reconcileBoardLayout`)
 * plus this boundary, a one-chip drop re-rendered every `motion` wrapper on the
 * board. That was ~90ms of the drop's frame on a 120-chip board and the reason a
 * release felt laggy. Every prop here is therefore either identity-stable or a
 * primitive that changes for the affected node alone.
 */
const BoardNode = memo(function BoardNode({
  node, items, now, draggedMemberId, expanded, placement, coarsePointer, reduce,
  onExpandedChange, onPointerDown, onPointerMove, onPointerEnd, onOpen, onHide,
}: BoardNodeProps) {
  const single = !node.clustered ? items[0]! : null;
  const singleDragged = !!single && draggedMemberId === single.task.id;
  const clusterMemberDragged = node.clustered && !!draggedMemberId;
  return (
    <motion.div
      data-matrix-drag-root={single ? "" : undefined}
      data-matrix-node={node.id}
      data-matrix-cluster={node.clustered ? node.id : undefined}
      className={cn(
        "group absolute",
        !singleDragged && !clusterMemberDragged && !expanded && "hover:!z-40",
      )}
      style={{
        left: `${node.x}px`,
        top: `${node.y}px`,
        x: "-50%",
        zIndex: singleDragged || clusterMemberDragged
          ? 50
          : expanded
            ? 45
            : 10 + Math.round((items[0]!.score ?? 0) * 20),
      }}
      initial={reduce ? false : { opacity: 0, scale: 0.5, y: "-50%" }}
      // The alignment translate is ANIMATED, not a static style: an
      // edge-aligned block re-anchors from centred to top/bottom the
      // moment it opens, and applying that instantly jumps it by half
      // its height before the growth even starts.
      animate={{ opacity: 1, scale: 1, y: ALIGN_TRANSLATE[placement?.align ?? "center"] }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.5 }}
      transition={reduce ? { duration: 0 } : { ...springs.snappy, y: springs.smooth }}
    >
      {single ? (
        <>
          <BoardChip
            item={single}
            now={now}
            dragged={singleDragged}
            coarsePointer={coarsePointer}
            layoutMaxWidth={node.width}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerEnd={onPointerEnd}
            onOpen={onOpen}
          />
          {!singleDragged && <HideControl item={single} onHide={onHide} />}
        </>
      ) : (
        <ClusterBlock
          node={node}
          items={items}
          now={now}
          expanded={expanded}
          expandedMaxHeight={placement?.maxHeight}
          coarsePointer={coarsePointer}
          draggedMemberId={draggedMemberId}
          onExpandedChange={onExpandedChange}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerEnd={onPointerEnd}
          onOpen={onOpen}
          onHide={onHide}
        />
      )}
    </motion.div>
  );
});

/**
 * The eye control at a chip's right edge: takes the chip's whole series off the
 * board/queue (`matrix_hidden`). Rendered as a SIBLING of the chip — the chip is
 * itself a `<button>`, so it cannot nest one — and absolutely positioned so it
 * never enters the packer's collision box (those px metrics are calibrated
 * against the rendered chip; a control that widened a chip would silently let
 * the packer certify overlapping layouts).
 *
 * Hover/focus reveal only: a permanently visible eye on every chip would cover
 * the titles a small board is already tight on. Touch input reaches the same
 * flag through the task sheet's matrix widget and the hidden list below.
 */
function HideControl({ item, onHide, scope = "node", className }: {
  item: ViewItem;
  onHide: (item: ViewItem) => void;
  /** Which `group` wrapper reveals it: a whole board/tray node, or one cluster row. */
  scope?: "node" | "row";
  className?: string;
}) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      data-matrix-hide
      title={t("priorities.hide.action")}
      aria-label={t("priorities.hide.action")}
      className={cn(
        "absolute top-1/2 z-30 grid h-[18px] w-[18px] -translate-y-1/2 place-items-center rounded-full border bg-card text-muted-foreground opacity-0 shadow-sm transition-opacity",
        // Invisible controls must not eat the chip's own pointer events.
        "pointer-events-none hover:text-foreground focus-visible:pointer-events-auto focus-visible:opacity-100",
        scope === "row"
          ? "group-hover/row:pointer-events-auto group-hover/row:opacity-100"
          : "group-hover:pointer-events-auto group-hover:opacity-100",
        className ?? "right-0.5",
      )}
      // The chip's drag gesture lives on a sibling, but a cluster row's press
      // target wraps a wide area — stop the press so a click here never drags.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onHide(item);
      }}
    >
      <EyeOff className="h-3 w-3" aria-hidden />
    </button>
  );
}

interface ClusterBlockProps {
  node: BoardLayoutNode;
  items: ViewItem[];
  now: Date;
  expanded: boolean;
  /** Viewport-aware cap while expanded (see `ExpandedPlacement`). */
  expandedMaxHeight?: number | undefined;
  coarsePointer: boolean;
  draggedMemberId: string | null;
  onExpandedChange: (node: BoardLayoutNode, expanded: boolean) => void;
  onPointerDown: (e: React.PointerEvent, id: string) => void;
  onPointerMove: (e: React.PointerEvent, id: string) => void;
  onPointerEnd: (e: React.PointerEvent, id: string) => void;
  onOpen: (task: Task) => void;
  onHide: (item: ViewItem) => void;
}

function ClusterBlock({
  node,
  items,
  now,
  expanded,
  expandedMaxHeight,
  coarsePointer,
  draggedMemberId,
  onExpandedChange,
  onPointerDown,
  onPointerMove,
  onPointerEnd,
  onOpen,
  onHide,
}: ClusterBlockProps) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const blockRef = useRef<HTMLDivElement | null>(null);
  const rowsRef = useRef<HTMLDivElement | null>(null);
  // The box is sized EXPLICITLY and springs between the two heights. Letting
  // Framer's `layout` projection own the size instead animates it as a SCALE,
  // which squashes and stretches every row's text on the way (and piles the
  // counter-scaled rows on top of each other while the box is still small).
  // `layout="position"` therefore stays on for repacking moves only.
  const [height, setHeight] = useState<number | null>(null);
  const [resizing, setResizing] = useState(false);
  const heightRef = useRef<number | null>(null);
  const visible = expanded ? items : items.slice(0, node.visibleCount);
  const hidden = items.length - visible.length;
  const canCollapse = expanded && items.length > node.visibleCount;
  const memberDragged = !!draggedMemberId;
  /** Rows height the cached {@link heightRef} was derived from — lets the
   *  observer re-target by DELTA instead of re-measuring (see below). */
  const rowsHeightRef = useRef<number | null>(null);
  /** Re-target the box at what it wants to be right now. The expanded cap is a
   *  real `max-height`, so the natural measurement already respects it. */
  const measure = useCallback((clip: boolean) => {
    const el = blockRef.current;
    if (!el) return;
    const applied = el.style.height;
    el.style.height = "auto";
    // `offsetHeight`, NOT a client rect: the node above runs a scale-in enter
    // animation, and a rect measured mid-flight comes back scaled.
    const natural = el.offsetHeight;
    el.style.height = applied;
    rowsHeightRef.current = rowsRef.current?.offsetHeight ?? null;
    if (natural === heightRef.current) return;
    // Only a REAL change arms the clip, so there is always a finishing
    // animation to disarm it again (a settled block must stay scrollable).
    if (clip && heightRef.current !== null) setResizing(true);
    heightRef.current = natural;
    setHeight(natural);
  }, []);
  // Row-count/text-scale changes re-target the box without a toggle. The
  // observed node is the row list, which our own height animation never
  // resizes (it clips instead), so this cannot feed itself.
  //
  // A repack fires this for every block whose width changed, and `measure`'s
  // `height: auto` round-trip forces a synchronous layout of the WHOLE document
  // each time (~7ms on a busy board) — usually to conclude the height did not
  // change at all. The observer already reports the new rows height for free, and
  // only the ROWS moved, so the block's natural height shifts by exactly that
  // delta. That keeps a board drop off the layout-thrash path entirely; the
  // toggle/first-run paths still take the authoritative measurement above.
  useLayoutEffect(() => {
    const rows = rowsRef.current;
    if (!rows) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      const rowsHeight = entry?.borderBoxSize?.[0]?.blockSize ?? entry?.contentRect.height ?? null;
      const knownRows = rowsHeightRef.current;
      const knownHeight = heightRef.current;
      if (rowsHeight === null || knownRows === null || knownHeight === null) {
        measure(false);
        return;
      }
      const delta = rowsHeight - knownRows;
      rowsHeightRef.current = rowsHeight;
      if (delta === 0) return;
      heightRef.current = knownHeight + delta;
      setHeight(knownHeight + delta);
    });
    observer.observe(rows);
    return () => observer.disconnect();
  }, [measure]);
  useLayoutEffect(() => {
    // The committed content is already the NEW state while `height` still holds
    // the old one, so the box is frozen at its pre-toggle size and springs from
    // there — no first frame at the wrong height, no mid-flight retarget.
    measure(true);
  }, [expanded, expandedMaxHeight, measure]);
  return (
    <motion.div
      ref={blockRef}
      layout={reduce ? false : "position"}
      animate={height === null ? {} : { height }}
      onAnimationComplete={() => setResizing(false)}
      transition={reduce ? { duration: 0 } : springs.smooth}
      className={cn(
        "relative rounded-lg border bg-card/95 p-0.5 shadow-md backdrop-blur-[2px]",
        expanded && "ring-1 ring-primary/30",
        memberDragged
          ? "overflow-visible"
          // Clipped while the height moves so rows are revealed/hidden by the
          // growing box instead of spilling out or flashing a scrollbar; a
          // settled COLLAPSED block keeps overflow visible so a hovered row can
          // still scale past the edge.
          : resizing
            ? "overflow-hidden"
            : expanded
              ? "overflow-y-auto overscroll-contain"
              : undefined,
      )}
      style={{ width: node.width, maxHeight: expanded ? expandedMaxHeight : undefined }}
    >
      <div ref={rowsRef} className="divide-y divide-border/60">
        {visible.map((item, index) => {
          const isDragged = draggedMemberId === item.task.id;
          // Collapsed, the control rides the last row so the block stays
          // line-free; expanded, it moves to the pinned footer below — a
          // scrolled-away "show less" is unreachable without scrolling back.
          const showsInlineOverflow = !expanded && index === visible.length - 1 && hidden > 0;
          return (
            <div
              key={item.task.id}
              className="group/row relative flex min-w-0 items-center"
            >
              <div
                data-matrix-drag-root
                className="min-w-0 flex-1"
                style={isDragged ? { zIndex: 70 } : undefined}
              >
                <BoardChip
                  item={item}
                  now={now}
                  dragged={isDragged}
                  clustered
                  coarsePointer={coarsePointer}
                  scrollableCluster={expanded}
                  clusterInlineOverflow={showsInlineOverflow}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerEnd={onPointerEnd}
                  onOpen={onOpen}
                />
              </div>
              {!isDragged && (
                <HideControl
                  item={item}
                  onHide={onHide}
                  scope="row"
                  // Clear of the inline "+N more" control when that row carries it.
                  className={showsInlineOverflow ? "right-9" : "right-0.5"}
                />
              )}
              {showsInlineOverflow && (
                <button
                  type="button"
                  aria-expanded={false}
                  aria-label={t("priorities.cluster.expand", { count: hidden })}
                  className={cn(
                    "relative z-10 mr-0.5 shrink-0 rounded bg-primary/10 px-1 text-[calc(10px*var(--app-text-scale))] font-medium leading-none text-primary before:absolute hover:bg-primary/15",
                    coarsePointer ? "py-1 before:-inset-2" : "py-0.5 before:-inset-1.5",
                  )}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    onExpandedChange(node, true);
                  }}
                >
                  {t("priorities.cluster.more", { count: hidden })}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {canCollapse && (
        <div className="sticky bottom-0 z-20 -mx-0.5 -mb-0.5 mt-0.5 rounded-b-lg border-t border-border/60 bg-card/95 p-0.5 backdrop-blur-[2px]">
          <button
            type="button"
            aria-expanded
            aria-label={t("priorities.cluster.collapse")}
            className={cn(
              "w-full rounded bg-primary/10 px-1 text-[calc(10px*var(--app-text-scale))] font-medium leading-none text-primary hover:bg-primary/15",
              coarsePointer ? "py-2" : "py-1",
            )}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onExpandedChange(node, false);
            }}
          >
            {t("calendar.showLess")}
          </button>
        </div>
      )}
    </motion.div>
  );
}

interface ChipProps {
  item: ViewItem;
  now: Date;
  dragged: boolean;
  inTray?: boolean;
  clustered?: boolean;
  /** Touch input — matches the taller rows the packer budgeted for. */
  coarsePointer?: boolean;
  scrollableCluster?: boolean;
  clusterInlineOverflow?: boolean;
  layoutMaxWidth?: number;
  onPointerDown: (e: React.PointerEvent, id: string) => void;
  onPointerMove: (e: React.PointerEvent, id: string) => void;
  onPointerEnd: (e: React.PointerEvent, id: string) => void;
  onOpen: (task: Task) => void;
}

/** Memoized: a single edit rebuilds the model, but `buildMatrixView`'s item
 *  cache keeps every untouched `ViewItem` identical, and the pointer handlers
 *  are stable — so only the chips that actually changed re-render. Framer nodes
 *  are expensive enough that re-rendering a board's worth of them per edit is
 *  what made a drop feel laggy. */
const BoardChip = memo(function BoardChip({ item, now, dragged, inTray, clustered, coarsePointer, scrollableCluster, clusterInlineOverflow, layoutMaxWidth, onPointerDown, onPointerMove, onPointerEnd, onOpen }: ChipProps) {
  const { t } = useI18n();
  const tf = useTimeFormat();
  const locale = useAppLocale();
  const reduce = useReducedMotion();
  const id = item.task.id;
  // Placed = continuous diagonal color; unplaced keeps its discrete flag hue
  // (the app-wide placed-gate rendering rule).
  const color = item.placed && item.pos
    ? matrixColorHex(matrixScoreOf(item.pos))
    : PRIORITY_FLAG_HEX[item.task.priority] || "#94a3b8";
  const auto = !!item.pos?.urgencyAuto || (!item.placed && !!item.pos);
  const due = item.task.due_at
    ? (item.task.all_day ? new Date(item.task.due_at).toLocaleDateString(locale) : tf.stamp(item.task.due_at))
    : null;
  const weeks = item.stale ? staleWeeks(item.task, now) : 0;
  const tip = [
    item.task.title || t("agenda.untitled"),
    due,
    item.stale ? t("priorities.stale.hint", { weeks }) : null,
  ].filter(Boolean).join(" · ");
  return (
    <motion.button
      type="button"
      title={tip}
      animate={{ scale: dragged ? 1.06 : 1 }}
      {...(!dragged && !reduce ? { whileHover: { scale: 1.05 } } : {})}
      transition={{
        scale: reduce ? { duration: 0 } : springs.snappy,
      }}
      className={cn(
        "flex items-center gap-1.5 border bg-card/95 shadow-sm",
        clustered
          ? cn(
              "w-full max-w-none gap-1 rounded-md border-transparent bg-transparent px-1 text-[calc(11px*var(--app-text-scale))] shadow-none hover:bg-accent/60 hover:shadow-none",
              coarsePointer ? "py-1.5" : "py-0.5",
              clusterInlineOverflow && "pr-0",
            )
          : cn(
              "max-w-[11rem] rounded-full text-xs",
              coarsePointer ? "px-2.5 py-1.5" : "px-2 py-1",
            ),
        !item.placed && !inTray && (
          clustered
            ? "border-dashed border-muted-foreground/40 opacity-75"
            : "border-dashed opacity-75"
        ),
        item.stale && !dragged && "opacity-80 saturate-[.75]",
        item.done && "opacity-60",
        dragged ? "cursor-grabbing shadow-lg ring-1 ring-primary/40" : "cursor-grab hover:shadow-md",
      )}
      style={{ touchAction: scrollableCluster ? "pan-y" : "none", maxWidth: layoutMaxWidth }}
      onPointerDown={(e) => onPointerDown(e, id)}
      onPointerMove={(e) => onPointerMove(e, id)}
      onPointerUp={(e) => onPointerEnd(e, id)}
      onPointerCancel={(e) => onPointerEnd(e, id)}
      onContextMenu={(e) => e.preventDefault()}
      // Pointer gestures handle mouse/touch opening; `detail === 0` = a real
      // keyboard activation (Enter/Space), which never went through them.
      onClick={(e) => { if (e.detail === 0) onOpen(item.task); }}
    >
      <span
        className={cn(
          "relative shrink-0 rounded-full",
          scrollableCluster && "before:absolute before:-inset-2",
        )}
        style={{ touchAction: scrollableCluster ? "none" : undefined }}
      >
        {item.done
          ? <Check className="h-3 w-3 text-muted-foreground" aria-hidden />
          : <span
              className={cn(
                "block rounded-full",
                clustered ? "h-2 w-2" : "h-2.5 w-2.5",
                item.overdue && "ring-2 ring-red-500/60",
              )}
              style={{ background: color }}
            />}
      </span>
      <span className={cn("min-w-0 truncate", item.done && "line-through text-muted-foreground")}>
        {item.task.title || <em className="text-muted-foreground">{t("agenda.untitled")}</em>}
      </span>
      {item.stale && (
        <span className="shrink-0 text-[calc(10px*var(--app-text-scale))] font-medium leading-none text-amber-600 dark:text-amber-400">
          {t("priorities.stale.badge", { weeks })}
        </span>
      )}
      {auto && item.placed && (
        <Clock className="h-3 w-3 shrink-0 text-muted-foreground/70" aria-label={t("matrix.autoUrgency.hint")} />
      )}
    </motion.button>
  );
});
